import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { GraphNode } from '@/lib/apiTypes'
import type { BlastRadius } from '@/features/graph/lib/graphView'
import {
  ExternalLinkIcon, CopyIcon, CheckIcon, AlertIcon, LockIcon, TrashIcon,
  CrosshairIcon, EyeOffIcon, UploadIcon, ChipIcon, DatabaseIcon, BoxIcon,
} from '@/shared/components/Icons'
import s from '@/features/graph/DepGraph.module.css'

/**
 * Right-click menu for a graph node.
 *
 * Extracted from DepGraph, which had it inline with three bare buttons and a dead-end
 * "No actions available" for anything unversioned. Three things were wrong with that beyond
 * the styling: it was mouse-only, it rendered at the raw cursor position so it opened
 * off-screen near the right or bottom edge, and it sat at a hardcoded z-index of 1000 while
 * the app has a named --z-* scale.
 */

const MENU_W = 236
/** Gap kept between the menu and the viewport edge when it flips. */
const EDGE_PAD = 8

export interface GraphContextMenuProps {
  x: number
  y: number
  node: GraphNode
  currentOrg?: string
  navigable: boolean
  blast?: BlastRadius
  onDismiss: () => void
  onIsolate: (n: GraphNode) => void
  onHide: (n: GraphNode) => void
  onLockFileHelp: (n: GraphNode) => void
  onDeprecate: (n: GraphNode) => void
  onUndeprecate: (n: GraphNode) => void
  onBlock: (n: GraphNode) => void
}

interface Item {
  key: string
  label: string
  icon: ReactNode
  onSelect: () => void
  danger?: boolean
  /** Starts a new group; renders a divider above. */
  group?: string
}

const KIND_GLYPH = {
  state: <DatabaseIcon size={12} />,
  module: <BoxIcon size={12} />,
  provider: <ChipIcon size={12} />,
} as const

const KIND_NOUN = { state: 'state file', module: 'module', provider: 'provider' } as const

export function GraphContextMenu({
  x, y, node, currentOrg, navigable, blast,
  onDismiss, onIsolate, onHide, onLockFileHelp, onDeprecate, onUndeprecate, onBlock,
}: GraphContextMenuProps) {
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x, top: y })
  const [active, setActive] = useState(0)
  const [copied, setCopied] = useState(false)

  // Flip rather than overflow. Measured after mount because the item count - and so the
  // height - depends on the node.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const h = el.offsetHeight
    const left = x + MENU_W + EDGE_PAD > window.innerWidth
      ? Math.max(EDGE_PAD, x - MENU_W)
      : x
    const top = y + h + EDGE_PAD > window.innerHeight
      ? Math.max(EDGE_PAD, y - h)
      : y
    setPos({ left, top })
  }, [x, y])

  const isOwned = !node.external && (currentOrg ? node.namespace === currentOrg : false)
  const hasVersion = !!node.latest_version && !node.external
  const canGovern = node.kind === 'module' && isOwned && hasVersion

  const address = node.kind === 'module'
    ? `${node.namespace}/${node.module_name}/${node.module_provider}`
    : node.kind === 'provider'
      ? `${node.namespace}/${node.provider_type}`
      : (node.workspace_name ?? node.label)

  function openDetail() {
    if (node.kind === 'module' && node.module_name && node.module_provider) {
      const v = node.latest_version ? `/${node.latest_version}` : ''
      navigate(`/org/${node.namespace}/modules/${node.module_name}/${node.module_provider}${v}`)
    } else if (node.kind === 'state' && node.workspace_name && currentOrg) {
      navigate(`/org/${currentOrg}/state/${encodeURIComponent(node.workspace_name)}`)
    } else if (node.kind === 'provider' && currentOrg) {
      // A provider node collapses both registries, and the detail route is keyed by registry
      // too - so land on the filtered list, which shows every row for this namespace/type.
      navigate(`/org/${currentOrg}/providers?q=${encodeURIComponent(`${node.namespace}/${node.provider_type}`)}`)
    }
  }

  const items: Item[] = []

  // ── Navigate ──
  if (navigable && !node.external) {
    items.push({
      key: 'open',
      label: `Open ${KIND_NOUN[node.kind]}`,
      icon: <ExternalLinkIcon size={13} />,
      onSelect: () => { openDetail(); onDismiss() },
    })
  }
  items.push({
    key: 'copy',
    label: copied ? 'Copied' : 'Copy address',
    icon: copied ? <CheckIcon size={13} /> : <CopyIcon size={13} />,
    onSelect: () => {
      navigator.clipboard?.writeText(address).then(
        () => setCopied(true),
        // A clipboard denial is not worth a toast here; the menu just does not confirm.
        () => undefined,
      )
    },
  })

  // ── Graph ──
  items.push({
    key: 'isolate',
    label: 'Isolate lineage',
    icon: <CrosshairIcon size={13} />,
    onSelect: () => onIsolate(node),
    group: 'graph',
  })
  items.push({
    key: 'hide',
    label: 'Hide from view',
    icon: <EyeOffIcon size={13} />,
    onSelect: () => onHide(node),
  })

  // ── Fix ──
  if (node.kind === 'state' && node.has_lock_file === false) {
    items.push({
      key: 'lockfile',
      label: 'Add a lock file…',
      icon: <UploadIcon size={13} />,
      onSelect: () => onLockFileHelp(node),
      group: 'fix',
    })
  }

  // ── Govern ──
  if (canGovern) {
    items.push(node.deprecated
      ? {
          key: 'undeprecate',
          label: `Undeprecate ${node.latest_version}`,
          icon: <CheckIcon size={13} />,
          onSelect: () => onUndeprecate(node),
          group: 'govern',
        }
      : {
          key: 'deprecate',
          label: `Deprecate ${node.latest_version}`,
          icon: <AlertIcon size={13} />,
          onSelect: () => onDeprecate(node),
          group: 'govern',
        })
    items.push({
      key: 'block',
      label: 'Block consumption',
      icon: node.blocked ? <LockIcon size={13} /> : <TrashIcon size={13} />,
      onSelect: () => onBlock(node),
      danger: true,
    })
  }

  // Dismiss on outside click or Escape; move with the arrows; activate with Enter.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) onDismiss()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onDismiss(); return }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const delta = e.key === 'ArrowDown' ? 1 : -1
        setActive((i) => (i + delta + items.length) % items.length)
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        items[active]?.onSelect()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onDismiss, items, active])

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={`Actions for ${address}`}
      className={s.contextMenu}
      style={{ left: pos.left, top: pos.top, width: MENU_W }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className={s.ctxHeader}>
        <span className={s.ctxHeaderKind}>{KIND_GLYPH[node.kind]}</span>
        <span className={s.ctxHeaderLabel}>{address}</span>
      </div>
      {blast && blast.total > 0 && (
        <div className={s.ctxHeaderSub}>{blast.total} depend on this</div>
      )}

      {items.map((item, i) => (
        <div key={item.key} className={item.group ? s.ctxGroup : undefined}>
          <button
            role="menuitem"
            className={`dep-graph-button ${s.ctxBtn} ${item.danger ? s.ctxBtnDanger : ''} ${i === active ? s.ctxBtnActive : ''}`}
            onMouseEnter={() => setActive(i)}
            onClick={item.onSelect}
          >
            <span className={s.ctxIcon}>{item.icon}</span>
            {item.label}
          </button>
        </div>
      ))}
    </div>
  )
}
